/**
 * 기사편집 v2 — 입력 검증 + 저장.
 *
 * v1 `checkForm()` 의 검증 항목을 그대로 옮기되 `document.inputform.xxx.value`
 * (name 기반 직접 접근)를 쓰지 않는다. v2 는 조합에 따라 **필드를 아예 렌더하지 않으므로**
 * 그런 접근은 존재하지 않는 요소에서 터진다. 항상 "있으면 검사"로 쓴다.
 *
 * 저장 스코프는 서버(MemberAdminSaveService)가 최종 결정한다. 화면이 렌더하지 않은 필드는
 * POST 에 실리지 않고, 서버 `pick()` 이 부재키를 건너뛰므로 값이 빈문자로 덮이지 않는다.
 *
 * 설계: .ai/domains/member-edit-v2.md §9
 */
export default class Save {
    constructor(page) {
        this.p = page;
    }

    /** 검사 범위 — 기본은 본 폼. 기사/장비 관리 모달의 기사편집 폼이 자기 범위를 넘긴다 */
    _root($root) { return $root && $root.length ? $root : $('#meForm'); }

    /** 폼 값 하나 — 없는 필드는 빈 문자열 */
    _v(name, $root) {
        const $el = this._root($root).find(`[name="${name}"]`);
        return $el.length ? String($el.val() ?? '') : '';
    }

    /** 그 폼이 이 필드를 그렸는가 — "있으면 검사" 원칙의 판정 (클래스 머리말 참고) */
    _has(name, $root) { return this._root($root).find(`[name="${name}"]`).length > 0; }

    /** 실패 알림 + 그 칸으로 포커스. id 가 아니라 **폼 안의 name** 으로 찾는다 —
     *  같은 검증을 다른 폼(모달)에서도 쓰므로 `#me_xxx` 를 박으면 거기선 포커스가 안 간다 */
    _fail(msg, name, $root) {
        $j.F.Admin.Util.tsalert('error', msg);
        this._root($root).find(`[name="${name}"]`).first().focus();
        return false;
    }

    /**
     * 검증. 실패하면 토스트 + 포커스 후 false.
     *
     * @param {jQuery} [$root]    검사할 폼 (없으면 본 폼)
     * @param {string} [editMode] 'edit'|'new' (없으면 페이지 모드)
     */
    validate($root, editMode) {
        const p    = this.p;
        const mode = editMode || p.editMode;

        // 소속 — 소유자만 편집한다 (소속자는 소유자 소속을 따른다)
        if (p.isOwner && this._has('sosok', $root)) {
            p.updateSosokCode();
            if (this._v('sosok', $root).length < 4) return this._fail('소속을 선택해 주세요.', 'sosok', $root);
        }

        if (this._v('mem_name', $root).length < 1) {
            return this._fail('이름을 입력해 주세요.', 'mem_name', $root);
        }
        if (this._has('mem_id', $root) && this._v('mem_id', $root).length < 4) {
            return this._fail('아이디를 입력해주세요.', 'mem_id', $root);
        }
        if (this._has('mem_permit', $root) && this._v('mem_permit', $root).length < 1) {
            return this._fail('기사권한을 입력해 주세요.', 'mem_permit', $root);
        }
        // 기사타입은 소유자 귀속 — 소유자 화면에서만 필수 검사한다
        if (this._v('mem_permit', $root) === '소유자'
            && this._has('mem_driver_type', $root) && this._v('mem_driver_type', $root).length < 1) {
            return this._fail('기사타입을 입력해 주세요.', 'mem_driver_type', $root);
        }

        // 비밀번호 — 신규는 필수, 편집은 입력했을 때만
        const pwd = this._v('mem_pwd', $root);
        if (mode === 'edit') {
            if (pwd.length > 0 && pwd.length < 4) {
                return this._fail('비밀번호를 4자이상 입력해 주세요', 'mem_pwd', $root);
            }
        } else if (pwd.length < 4) {
            return this._fail('비밀번호를 4자이상 입력해 주세요', 'mem_pwd', $root);
        }

        // 형식 + 실재하는 날짜인지까지 본다. 예전 전역 is_valid_date 는 정규식뿐이라
        // 2026-02-30 · 2026-13-01 같은 값을 통과시켰다.
        const birth = this._v('mem_birthday', $root);
        const birthOk = dayjs(birth).isValid() && dayjs(birth).format('YYYY-MM-DD') === birth;
        if (birth && !birthOk) {
            return this._fail('생년월일이 올바른 날짜(YYYY-MM-DD)가 아닙니다.', 'mem_birthday', $root);
        }

        // 차고지 주소가 있으면 GPS 도 있어야 매칭이 된다.
        // 기사타입 칸이 없는 폼(모달)에서는 차고지 칸이 곧 수발주자라는 뜻이므로 그대로 검사한다.
        if (this._has('car_parkaddress', $root)
            && (!this._has('mem_driver_type', $root) || this._v('mem_driver_type', $root) === '수발주자')
            && this._v('car_parkaddress', $root) && !this._v('gps', $root)) {
            $j.F.Admin.Util.tsalert('error', '차고지 GPS 값이 제대로 설정이 안되었습니다. 차고지를 다시 설정해주세요.');
            return false;
        }

        return true;
    }

    /** 저장 */
    /**
     * 위치동의를 바꿔서 저장하려는 경우의 확인.
     *
     * 다른 필드와 달리 되돌릴 수 없다 — 동의이력(g5_terms_consent_log)은 append-only 라
     * 잘못 고르고 저장하면 지우지 못하고 반대 이력을 덧쌓는 수밖에 없다. 그래서 여기서만
     * 한 번 더 묻는다. 값이 그대로면 아무것도 묻지 않는다(저장할 때마다 뜨면 눌러 넘기게 된다).
     */
    confirmLocConsent() {
        const $sel = $('#me_loc_consent');
        if (!$sel.length) return true;

        const val  = String($sel.val() || '');
        const prev = String($sel.attr('data-prev') || 'none');
        if (val === prev || (val !== 'agree' && val !== 'refuse')) return true;

        return confirm(val === 'agree'
            ? '회원과 통화하여 위치정보 활용 동의를 받으셨습니까?\n\n'
              + '· 위치기반서비스 이용약관\n· 개인위치정보 수집·이용 동의\n\n'
              + '저장하면 2건이 동의 이력으로 함께 기록됩니다.'
            : '회원이 위치정보 활용에 동의하지 않으셨습니까?\n\n저장하면 미동의 이력으로 기록됩니다.');
    }

    /** 마케팅동의를 바꿔서 저장하려는 경우의 확인 — 위치동의와 같은 이유(되돌릴 수 없는 이력) */
    confirmMkConsent() {
        const $sel = $('#me_mk_consent');
        if (!$sel.length) return true;

        const val  = String($sel.val() || '');
        const prev = String($sel.attr('data-prev') || 'none');
        if (val === prev || (val !== 'agree' && val !== 'refuse')) return true;

        return confirm(val === 'agree'
            ? '회원과 통화하여 작업사진 활용 동의(현장사진 홍보 활용)를 받으셨습니까?\n\n저장하면 동의 이력으로 기록됩니다.'
            : '회원이 작업사진 활용에 동의하지 않으셨습니까?\n\n저장하면 미동의 이력으로 기록됩니다.');
    }

    submit() {
        if (!this.validate()) return;
        if (!this.confirmLocConsent()) return;
        if (!this.confirmMkConsent()) return;

        const p = this.p;
        const $btn = $('#meSave');
        $btn.prop('disabled', true).text('저장중...');

        $.ajax({
            type: 'POST',
            url: '/adm/ajax/member/v2/save',
            data: $('#meForm').serialize(),
            dataType: 'json',
        })
            .done((d) => {
                if (d.result) {
                    // 화면 중앙 — 저장 직후 위로 스크롤 안 해도 바로 보이게
                    $j.Ui.Toast.show(d.message, { type: 'success', place: 'center' });
                    // 저장됐으니 지금 값이 새 기준 — 아래 신규등록 새로고침에서 창 닫기 경고가 뜨지 않게 먼저 한다
                    p.act.companyCommit();
                    // 비밀번호는 1회성 — 칸에 남겨두면 이후 다른 항목을 저장할 때마다 다시 나가
                    // 회원이 앱에서 바꾼 비밀번호를 되돌리고 변경 강제(pwd_reset_at)를 다시 켠다
                    $('#me_mem_pwd').val('');
                    $('#me_pwd_reset').val('');

                    // 신규 등록만 새로고침한다 — mb_no 가 새로 생겨야 편집모드(장비연결·서류·상담)가
                    // 열린다. 기존 회원 편집은 이미 그 상태라 새로고침 없이 토스트로 끝낸다.
                    if (p.editMode !== 'edit') {
                        setTimeout(() => location.replace(`/adm/member/edit?mb_no=${d.mb_no}`), 1500);
                        return;
                    }
                    $btn.prop('disabled', false).text('저장');
                    return;
                }
                $j.F.Admin.Util.tsalert('error', d.message);
                $btn.prop('disabled', false).text('저장');
            })
            .fail((xhr) => {
                $j.F.Admin.Util.tsalert('error', `저장 중 오류가 발생했습니다. (${xhr.status})`);
                $btn.prop('disabled', false).text('저장');
            });
    }
}
